import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  SmartToy as AIIcon,
  Assessment as AnalyticsIcon,
  Group as TeamIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { AIConsole } from '../../components/ai-console/AIConsole';
import ActiveProjects from '../../components/pm/ActiveProjects';
import CommunicationCenter from '../../components/pm/CommunicationCenter';
import Communications from '../../components/pm/Communications';
import ProjectTimeline from '../../components/pm/ProjectTimeline';
import ProjectAnalytics from '../../components/pm/ProjectAnalytics';
import TeamManagement from '../../components/pm/TeamManagement';
import QATesting from '../../components/pm/QATesting';
import FilesManagement from '../../components/pm/FilesManagement';
import Reports from '../../components/pm/Reports';
import Settings from '../../components/pm/Settings';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pm-tabpanel-${index}`}
      aria-labelledby={`pm-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * PM Dashboard Page
 * 
 * Comprehensive dashboard for Project Managers with:
 * - Active Projects overview
 * - AI Console for specification generation and project assistance
 * - Communication center for client and team messaging
 * - Project timeline and analytics
 * - Team management
 * - QA testing oversight
 * - File management
 * - Reports and settings
 */
const PMDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Project Manager Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage projects, communicate with clients, and leverage AI assistance
        </Typography>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="pm dashboard tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<DashboardIcon />} label="Projects" />
          <Tab icon={<AIIcon />} label="AI Console" />
          <Tab icon={<MessageIcon />} label="Communication" />
          <Tab icon={<AnalyticsIcon />} label="Analytics" />
          <Tab icon={<TeamIcon />} label="Team" />
          <Tab icon={<SettingsIcon />} label="Settings" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <ActiveProjects />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Project Timeline
                </Typography>
                <ProjectTimeline />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  QA Testing
                </Typography>
                <QATesting />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Files Management
                </Typography>
                <FilesManagement />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ height: '70vh', minHeight: '600px' }}>
            <AIConsole
              defaultContextType="project"
              height="100%"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="body2">
                <strong>AI Console Features:</strong>
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                <li>Generate project constitutions, specifications, and technical plans</li>
                <li>Get AI assistance with estimation and planning</li>
                <li>Ask technical questions about project requirements</li>
                <li>Receive suggestions for project improvements</li>
                <li>Context-aware responses based on your active projects</li>
              </Typography>
            </Paper>
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Communications />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <ProjectAnalytics />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Reports
                </Typography>
                <Reports />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <TeamManagement />
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          <Settings />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default PMDashboard;
